/**
 * 处理Black Friday页面的购物车功能
 */
class HalloweenCartManager {
  constructor() {
    this.isSubmitting = false;
    this.init();
  }

  /**
   * 初始化购物车管理器
   */
  init() {
    this.bindEvents();
  }

  /**
   * 绑定事件监听器
   */
  bindEvents() {
    // 使用事件委托处理所有购买按钮的点击事件
    document.addEventListener("click", (event) => {
      const buyButton = event.target.closest(
        ".black-friday_mini-robots-deal-item__btn--primary, .black-friday_max-deal-card__btn--primary, .black-friday_hot-deal-item__btn--primary",
      );

      if (buyButton) {
        // 防止事件冒泡和默认行为
        event.preventDefault();
        event.stopPropagation();

        // 检查是否已经在处理中
        if (buyButton.hasAttribute("data-processing")) {
          return;
        }

        this.handleBuyButtonClick(buyButton);
        return;
      }

      // 处理万圣节横幅背景图片点击事件
      const bannerImage = event.target.closest(
        ".black-friday_rewards__cta",
      );
      if (bannerImage) {
        event.preventDefault();
        this.handleBannerImageClick();
      }
    });
  }

  /**
   * 处理购买按钮点击事件
   * @param {HTMLElement} button - 被点击的按钮元素
   */
  async handleBuyButtonClick(button) {
    if (this.isSubmitting || button.hasAttribute("data-processing")) {
      return;
    }
    // console.log("点击购买按钮:", button);
    try {
      this.isSubmitting = true;
      button.setAttribute("data-processing", "true");
      this.setButtonLoading(button, true);

      // 获取商品ID
      const productIds = this.getProductIds(button);

      if (!productIds || productIds.length === 0) {
        throw new Error("未找到有效的商品ID");
      }

      // 添加商品到购物车
      await this.addProductsToCart(productIds);
      // 新窗口打开购物车页面
      // window.open("/cart", "_blank");
      window.location.href = "/cart";
      return false
      // 触发购物车更新事件
      this.triggerCartUpdate();
    } catch (error) {
      console.error("添加商品到购物车失败:", error);
    } finally {
      this.isSubmitting = false;
      button.removeAttribute("data-processing");
      this.setButtonLoading(button, false);
    }
  }

  /**
   * 获取商品ID
   * @param {HTMLElement} button - 按钮元素
   * @returns {Array} 商品ID数组
   */
  getProductIds(button) {
    const dataId = button.getAttribute("data-id");

    if (!dataId) {
      return [];
    }

    // 支持逗号分隔的多个ID（用于组合商品）
    return dataId
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id);
  }

  /**
   * 添加商品到购物车
   * @param {Array} productIds - 商品ID数组
   */
  async addProductsToCart(productIds) {
    // 构建购物车项目数组
    const items = productIds.map((id) => ({
      id: id,
      quantity: 1,
    }));

    // 使用现有的购物车添加方法
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || "添加商品失败");
    }

    return await response.json();
  }

  /**
   * 设置按钮加载状态
   * @param {HTMLElement} button - 按钮元素
   * @param {boolean} loading - 是否显示加载状态
   */
  setButtonLoading(button, loading) {
    if (loading) {
      button.setAttribute("aria-busy", "true");
      button.style.pointerEvents = "none";
      button.style.opacity = "0.7";
    } else {
      button.removeAttribute("aria-busy");
      button.style.pointerEvents = "";
      button.style.opacity = "";
    }
  }

  /**
   * 触发购物车更新事件
   */
  async triggerCartUpdate() {
    try {
      // 获取最新的购物车数据
      const cartResponse = await fetch("/cart.js");
      const cartData = await cartResponse.json();

      // 触发购物车变更事件
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartData,
          },
        }),
      );

      // 商品已成功添加到购物车
      this.showCartSuccessNotification();
      
    } catch (error) {
      console.error("更新购物车数据失败:", error);
    }
  }

  /**
   * 显示加购成功提示
   */
  showCartSuccessNotification() {
    // 检查万圣节成功提示管理器是否可用
    if (typeof showHalloweenCartSuccess === 'function') {
      showHalloweenCartSuccess({
        title: 'Check your cart now — your robot is ready!',
        message: '',
        duration: 3000
      });
    } else {
      // 降级处理：使用简单的alert提示
      // console.log('万圣节加购成功提示管理器未加载，使用降级提示');
      // 可以在这里添加其他降级提示方式
    }
  }

  /**
   * 处理横幅图片点击事件
   */
  handleBannerImageClick() {
    // 打开指定的链接
    window.SmileUI.openPanel();
  }
}

// 确保只初始化一次购物车管理器
let cartManager = null;

function initCartManager() {
  if (!cartManager) {
    cartManager = new HalloweenCartManager();
  }
}

/**
 * Fans Saying Video Autoplay Manager
 * 处理粉丝说部分的视频自动播放功能
 */
class FansSayingVideoManager {
  constructor() {
    this.videos = [];
    this.currentVideoIndex = 0;
    this.isPlaying = false;
    this.hasStarted = false;
    this.observer = null;
    this.sectionElement = null; // 缓存 DOM 元素
    this.eventHandlers = new Map(); // 存储事件处理器引用
    this.retryCount = 0; // 重试计数器
    this.maxRetries = 3; // 最大重试次数
    this.videoLoadTimeout = 10000; // 视频加载超时时间（可配置）
    this.init();
  }

  /**
   * 初始化视频管理器
   */
  init() {
    this.sectionElement = document.getElementById("fans-saying");
    if (!this.sectionElement) return;
    
    this.findVideos();
    this.setupIntersectionObserver();
    this.bindVideoEvents();
  }

  /**
   * 查找所有视频元素
   */
  findVideos() {
    if (!this.sectionElement) return;

    this.videos = Array.from(this.sectionElement.querySelectorAll("video"));
  }

  /**
   * 设置交叉观察器
   */
  setupIntersectionObserver() {
    if (!this.sectionElement) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasStarted) {
            // 立即设置标志，防止竞态条件
            this.hasStarted = true;
            this.startVideoSequence();
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -10% 0px",
      },
    );

    this.observer.observe(this.sectionElement);
  }

  /**
   * 绑定视频事件
   */
  bindVideoEvents() {
    this.videos.forEach((video, index) => {
      // 检查是否已经绑定，防止重复绑定
      if (video.dataset.eventsBound === 'true') return;
      
      // 确保视频静音
      video.muted = true;

      // 创建事件处理器（使用命名函数以便后续移除）
      const onEnded = () => this.onVideoEnded(index);
      const onError = (e) => {
        console.error(`视频 ${index + 1} 播放错误:`, e);
        this.onVideoError(index);
      };

      // 绑定事件
      video.addEventListener("ended", onEnded);
      video.addEventListener("error", onError);

      // 存储事件处理器引用，便于后续移除
      this.eventHandlers.set(video, { onEnded, onError });
      
      // 标记已绑定
      video.dataset.eventsBound = 'true';
    });
  }

  /**
   * 移除视频事件监听器
   */
  unbindVideoEvents() {
    this.videos.forEach((video) => {
      const handlers = this.eventHandlers.get(video);
      if (handlers) {
        video.removeEventListener("ended", handlers.onEnded);
        video.removeEventListener("error", handlers.onError);
        delete video.dataset.eventsBound;
      }
    });
    this.eventHandlers.clear();
  }
  /**
   * 等待所有视频加载完成
   */
  async waitForVideosToLoad() {

    const loadPromises = this.videos.map((video, index) => {
      return new Promise((resolve, reject) => {
        // 如果视频已经加载完成
        if (video.readyState >= 2) {
          resolve();
          return;
        }

        // 使用可配置的超时时间
        const timeout = setTimeout(() => {
          resolve();
        }, this.videoLoadTimeout);

        // 监听加载完成事件
        const onLoadedData = () => {
          clearTimeout(timeout);
          video.removeEventListener("loadeddata", onLoadedData);
          video.removeEventListener("canplay", onCanPlay);
          resolve();
        };

        // 监听可以播放事件
        const onCanPlay = () => {
          clearTimeout(timeout);
          video.removeEventListener("loadeddata", onLoadedData);
          video.removeEventListener("canplay", onCanPlay);
          resolve();
        };

        // 监听错误事件
        const onError = (error) => {
          console.warn(`视频 ${index + 1} 加载失败:`, error);
          clearTimeout(timeout);
          video.removeEventListener("loadeddata", onLoadedData);
          video.removeEventListener("canplay", onCanPlay);
          video.removeEventListener("error", onError);
          resolve(); // 即使出错也继续执行
        };

        video.addEventListener("loadeddata", onLoadedData);
        video.addEventListener("canplay", onCanPlay);
        video.addEventListener("error", onError);

        // 如果视频还没有开始加载，触发加载
        if (video.readyState === 0) {
          video.load();
        }
      });
    });

    // 等待所有视频加载完成
    await Promise.all(loadPromises);
  }

  /**
   * 开始视频播放序列
   */
  async startVideoSequence() {
    if (this.videos.length === 0) return;

    this.currentVideoIndex = 0;
    this.retryCount = 0; // 重置重试计数器

    try {
      // 等待所有视频加载完成
      await this.waitForVideosToLoad();
      
      // 设置播放状态（在实际开始播放后）
      this.isPlaying = true;
      
      // 播放第一个视频
      await this.playVideo(0);
    } catch (error) {
      console.error("视频序列启动失败:", error);
      this.isPlaying = false;
      this.hasStarted = false;
    }
  }

  /**
   * 播放指定索引的视频
   * @param {number} index - 视频索引
   */
  async playVideo(index) {
    if (index >= this.videos.length) {
      this.isPlaying = false;
      return;
    }

    const video = this.videos[index];
    if (!video) return;

    try {
      // 重置视频到开始位置
      video.currentTime = 0;

      // 播放视频
      await video.play();

      // 添加播放状态类
      video.classList.add("playing");
    } catch (error) {
      console.error(`播放视频 ${index + 1} 失败:`, error);
      this.onVideoError(index);
    }
  }

  /**
   * 视频播放结束处理
   * @param {number} index - 视频索引
   */
  onVideoEnded(index) {
    // 移除播放状态类（带边界检查）
    if (this.videos[index]) {
      this.videos[index].classList.remove("playing");
    }

    // 重置重试计数器
    this.retryCount = 0;

    // 检查是否是最后一个视频
    if (index === this.videos.length - 1) {
      this.currentVideoIndex = 0;
      this.playVideo(this.currentVideoIndex);
    } else {
      this.currentVideoIndex = index + 1;
      this.playVideo(this.currentVideoIndex);
    }
  }

  /**
   * 视频播放错误处理
   * @param {number} index - 视频索引
   */
  onVideoError(index) {
    // 移除播放状态类（带边界检查）
    if (this.videos[index]) {
      this.videos[index].classList.remove("playing");
    }

    // 检查重试次数，防止无限循环
    this.retryCount++;
    if (this.retryCount >= this.maxRetries) {
      console.error(`视频播放失败次数过多 (${this.retryCount}/${this.maxRetries})，停止播放`);
      this.isPlaying = false;
      return;
    }

    // 检查是否是最后一个视频
    if (index === this.videos.length - 1) {
      this.currentVideoIndex = 0;
      this.playVideo(this.currentVideoIndex);
    } else {
      this.currentVideoIndex = index + 1;
      this.playVideo(this.currentVideoIndex);
    }
  }

  /**
   * 停止所有视频播放
   */
  stopAllVideos() {
    this.videos.forEach((video) => {
      video.pause();
      video.currentTime = 0;
      video.classList.remove("playing");
    });

    this.isPlaying = false;
    this.currentVideoIndex = 0;
  }

  /**
   * 重置视频管理器
   */
  reset() {
    this.stopAllVideos();
    this.hasStarted = false;
    this.retryCount = 0;
  }

  /**
   * 销毁视频管理器
   */
  destroy() {
    // 断开交叉观察器
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    // 移除所有事件监听器
    this.unbindVideoEvents();
    
    // 停止所有视频
    this.stopAllVideos();
    
    // 清理引用
    this.videos = [];
    this.sectionElement = null;
  }
}

// 确保只初始化一次视频管理器
let videoManager = null;

function initVideoManager() {
  if (!videoManager) {
    videoManager = new FansSayingVideoManager();
  }
}

/**
 * Reviews Carousel Cloner
 * 复制评论轮播内容以实现无限滚动效果
 */
class ReviewsCarouselCloner {
  constructor() {
    this.init();
  }

  /**
   * 初始化轮播克隆器
   */
  init() {
    this.cloneCarouselContent();
  }

  /**
   * 查找所有评论轮播轨道并复制内容
   */
  cloneCarouselContent() {
    // 查找所有轮播轨道元素
    const tracks = document.querySelectorAll('.black-friday_reviews-carousel__track');
    
    if (tracks.length === 0) {
      console.log('未找到评论轮播轨道元素');
      return;
    }

    // 遍历每个轨道并复制内容
    tracks.forEach((track, index) => {
      this.cloneTrackContent(track, index);
    });

    // console.log(`已复制 ${tracks.length} 个评论轮播轨道的内容`);
  }

  /**
   * 复制单个轨道的内容
   * @param {HTMLElement} track - 轨道元素
   * @param {number} index - 轨道索引
   */
  cloneTrackContent(track, index) {
    try {
      // 获取轨道内的所有子元素
      const children = Array.from(track.children);
      
      if (children.length === 0) {
        console.log(`轨道 ${index + 1} 没有子元素`);
        return;
      }

      // 克隆每个子元素并添加到轨道末尾
      children.forEach((child) => {
        const clone = child.cloneNode(true);
        // 添加克隆标识
        clone.setAttribute('data-cloned', 'true');
        track.appendChild(clone);
      });

      // console.log(`轨道 ${index + 1}: 已复制 ${children.length} 个元素`);
    } catch (error) {
      console.error(`复制轨道 ${index + 1} 内容失败:`, error);
    }
  }

  /**
   * 移除所有克隆的元素（用于重置）
   */
  removeClonedContent() {
    const clonedElements = document.querySelectorAll('[data-cloned="true"]');
    clonedElements.forEach(element => element.remove());
    console.log(`已移除 ${clonedElements.length} 个克隆元素`);
  }

  /**
   * 重新克隆内容
   */
  refresh() {
    this.removeClonedContent();
    this.cloneCarouselContent();
  }
}

// 确保只初始化一次评论轮播克隆器
let carouselCloner = null;

function initCarouselCloner() {
  if (!carouselCloner) {
    carouselCloner = new ReviewsCarouselCloner();
  }
}

// 页面加载完成后初始化购物车管理器、视频管理器和轮播克隆器
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initCartManager();
    initVideoManager();
    initCarouselCloner();
  });
} else {
  initCartManager();
  initVideoManager();
  initCarouselCloner();
}
